import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Phone, RefreshCw, Send, ShoppingCart, Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SMSTab() {
  const token = sessionStorage.getItem('admin_token');
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [blasts, setBlasts] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, subsRes, blastsRes] = await Promise.all([
        fetch(`${API_URL}/api/sms/stats`, { headers }),
        fetch(`${API_URL}/api/sms/subscribers`, { headers }),
        fetch(`${API_URL}/api/sms/blasts`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (subsRes.ok) { const d = await subsRes.json(); setSubscribers(d.subscribers || []); }
      if (blastsRes.ok) { const d = await blastsRes.json(); setBlasts(d.blasts || []); }
    } catch (err) { console.error('SMS dashboard fetch failed:', err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendBlast = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/sms/blast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setResult(data);
      setMessage('');
      fetchData();
    } catch (err) {
      setResult({ status: 'error', error: err.message });
    } finally { setSending(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading SMS data...</div>;

  return (
    <div className="space-y-6" data-testid="sms-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Subscribers', val: stats?.total_subscribers || 0, color: 'text-emerald-600' },
          { label: 'Total Sent', val: stats?.total_sent || 0, color: 'text-blue-600' },
          { label: 'Failed', val: stats?.total_failed || 0, color: 'text-red-500' },
          { label: 'Sent This Month', val: stats?.sent_this_month || 0, color: 'text-purple-600' },
          { label: 'Blasts This Month', val: stats?.blasts_this_month || 0, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Send Blast */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="sms-blast-form">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Send className="w-4 h-4" /> Send Promotional SMS Blast
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Message will be sent to all {stats?.total_subscribers || 0} opted-in subscribers.
          Monthly limit: {stats?.monthly_limit_per_subscriber || 8}/subscriber.
        </p>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Flash sale! 30% off all hoodies this weekend only. Use code FLASH30 at ninjathlete.com/shop"
          rows={3}
          maxLength={320}
          className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-blue-400 focus:outline-none resize-none"
          data-testid="sms-blast-message"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">{message.length}/320 chars</span>
          <button
            onClick={sendBlast}
            disabled={!message.trim() || sending}
            className="px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="sms-blast-send-btn"
          >
            {sending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'Sending...' : 'Send Blast'}
          </button>
        </div>
        {result && (
          // eslint-disable-next-line no-nested-ternary -- readable tier picker
          <div className={`mt-3 p-3 rounded-lg text-sm ${result.status === 'complete' ? 'bg-emerald-50 text-emerald-700' : result.status === 'error' ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-700'}`} data-testid="sms-blast-result">
            {result.status === 'complete' && `Blast sent! ${result.sent} delivered, ${result.failed} failed, ${result.skipped} skipped.`}
            {result.status === 'error' && `Error: ${result.error}`}
            {result.status === 'no_subscribers' && 'No SMS subscribers yet.'}
          </div>
        )}
      </div>

      {/* Blast History */}
      {blasts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Blast History</h3>
          <div className="space-y-2">
            {blasts.slice(0, 10).map((b, i) => (
              <div key={b.created_at} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 truncate">{b.message?.substring(0, 80)}...</p>
                  <p className="text-xs text-gray-400">{new Date(b.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-3 text-xs ml-4">
                  <span className="text-emerald-600">{b.sent} sent</span>
                  {b.failed > 0 && <span className="text-red-500">{b.failed} failed</span>}
                  {b.skipped > 0 && <span className="text-gray-400">{b.skipped} skipped</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscribers List */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">SMS Subscribers ({subscribers.length})</h3>
        {subscribers.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b">
                <th className="pb-2">Phone</th>
                <th className="pb-2">Email</th>
                <th className="pb-2">Opted In</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.slice(0, 50).map((s, i) => (
                <tr key={s.phone} className="border-b border-gray-50">
                  <td className="py-2 font-mono text-gray-700">{s.phone}</td>
                  <td className="py-2 text-gray-600">{s.email || '—'}</td>
                  <td className="py-2 text-gray-400 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-400 text-sm">No SMS subscribers yet.</p>
        )}
      </div>

      {/* ─── Schedule SMS ─── */}
      <ScheduledSMSSection token={token} onRefresh={fetchData} />

      {/* ─── Triggers ─── */}
      <SMSTriggersSection token={token} />
    </div>
  );
}

function ScheduledSMSSection({ token, onRefresh }) {
  const [msg, setMsg] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState([]);

  const fetchScheduled = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sms/scheduled`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setScheduled(d.scheduled || []); }
    } catch (err) { console.error('Fetch scheduled SMS failed:', err); }
  }, [token]);

  useEffect(() => { fetchScheduled(); }, [fetchScheduled]);

  const handleSchedule = async () => {
    if (!msg.trim() || !scheduledFor || scheduling) return;
    setScheduling(true);
    try {
      const res = await fetch(`${API_URL}/api/sms/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg.trim(), scheduled_for: new Date(scheduledFor).toISOString() }),
      });
      if (res.ok) { setMsg(''); setScheduledFor(''); fetchScheduled(); }
    } catch (err) { console.error('Schedule SMS failed:', err); } finally { setScheduling(false); }
  };

  const cancelScheduled = async (id) => {
    try {
      await fetch(`${API_URL}/api/sms/scheduled/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchScheduled();
    } catch (err) { console.error('Cancel scheduled SMS failed:', err); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="scheduled-sms-section">
      <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4" /> Schedule SMS Blast
      </h3>
      <p className="text-xs text-gray-500 mb-4">Queue a message to send at a specific date &amp; time.</p>
      <div className="space-y-3">
        <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="Your scheduled message..." rows={2} maxLength={320}
          className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-blue-400 focus:outline-none resize-none" data-testid="schedule-sms-message" />
        <div className="flex items-center gap-3">
          <input type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-testid="schedule-sms-datetime" />
          <span className="text-xs text-gray-400">{msg.length}/320</span>
          <button onClick={handleSchedule} disabled={!msg.trim() || !scheduledFor || scheduling}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="schedule-sms-btn">
            <Calendar className="w-3.5 h-3.5" /> {scheduling ? 'Scheduling...' : 'Schedule'}
          </button>
        </div>
      </div>
      {scheduled.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Queued Blasts</p>
          {scheduled.filter(s => s.status === 'scheduled').map((s, i) => (
            <div key={s.scheduled_for} className="flex items-center justify-between text-sm border border-gray-100 rounded-lg p-3">
              <div>
                <p className="text-gray-700">{s.message?.substring(0, 60)}...</p>
                <p className="text-xs text-gray-400">Sends: {new Date(s.scheduled_for).toLocaleString()} &bull; {s.total_subscribers} subscribers</p>
              </div>
              <button onClick={() => cancelScheduled(s.schedule_id)} className="text-red-400 hover:text-red-600 text-xs font-semibold">Cancel</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SMSTriggersSection({ token }) {
  const [productName, setProductName] = useState('');
  const [saleName, setSaleName] = useState('');
  const [saleDiscount, setSaleDiscount] = useState('20%');
  const [saleCode, setSaleCode] = useState('');
  const [saleEnds, setSaleEnds] = useState('');
  const [sending, setSending] = useState('');
  const [triggerResult, setTriggerResult] = useState(null);

  const triggerNewProduct = async () => {
    if (!productName.trim()) return;
    setSending('product');
    try {
      const res = await fetch(`${API_URL}/api/sms/trigger/new-product`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_name: productName.trim() }),
      });
      const data = await res.json();
      setTriggerResult({ type: 'product', ...data });
      setProductName('');
    } catch (err) { console.error('Trigger new-product SMS failed:', err); } finally { setSending(''); }
  };

  const triggerFlashSale = async () => {
    if (!saleName.trim() || !saleEnds) return;
    setSending('sale');
    try {
      const res = await fetch(`${API_URL}/api/sms/trigger/flash-sale`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sale_name: saleName.trim(), discount: saleDiscount, code: saleCode, ends_at: new Date(saleEnds).toISOString() }),
      });
      const data = await res.json();
      setTriggerResult({ type: 'sale', ...data });
      setSaleName(''); setSaleCode(''); setSaleEnds('');
    } catch (err) { console.error('Trigger flash-sale SMS failed:', err); } finally { setSending(''); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sms-triggers-section">
      {/* New Product Trigger */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" /> New Product Drop
        </h3>
        <p className="text-xs text-gray-500 mb-3">Notify all SMS subscribers about a new product.</p>
        <input type="text" value={productName} onChange={e => setProductName(e.target.value)}
          placeholder="Product name (e.g., Titan Hoodie V2)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:border-blue-400 focus:outline-none" data-testid="trigger-product-name" />
        <button onClick={triggerNewProduct} disabled={!productName.trim() || sending === 'product'}
          className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50" data-testid="trigger-product-btn">
          {sending === 'product' ? 'Sending...' : 'Send Product Drop SMS'}
        </button>
      </div>

      {/* Flash Sale Trigger */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Zap className="w-4 h-4" /> Flash Sale
        </h3>
        <p className="text-xs text-gray-500 mb-3">Announce + auto-remind 1 hour before expiry.</p>
        <div className="space-y-2 mb-3">
          <input type="text" value={saleName} onChange={e => setSaleName(e.target.value)} placeholder="Sale name"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-testid="trigger-sale-name" />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={saleDiscount} onChange={e => setSaleDiscount(e.target.value)} placeholder="Discount (e.g., 30%)"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
            <input type="text" value={saleCode} onChange={e => setSaleCode(e.target.value)} placeholder="Promo code (optional)"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
          </div>
          <input type="datetime-local" value={saleEnds} onChange={e => setSaleEnds(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-testid="trigger-sale-ends" />
        </div>
        <button onClick={triggerFlashSale} disabled={!saleName.trim() || !saleEnds || sending === 'sale'}
          className="w-full px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid="trigger-sale-btn">
          {sending === 'sale' ? 'Sending...' : 'Launch Flash Sale SMS'}
        </button>
      </div>

      {triggerResult && (
        <div className="col-span-full bg-emerald-50 text-emerald-700 p-3 rounded-lg text-sm" data-testid="trigger-result">
          {triggerResult.type === 'product' ? 'Product drop' : 'Flash sale'} blast: {triggerResult.sent} sent, {triggerResult.failed} failed
          {triggerResult.reminder_scheduled && ' — 1hr reminder auto-scheduled'}
        </div>
      )}
    </div>
  );
}
